import { useState, type FormEvent } from "react";
import { Navigate, Link, useLocation } from "react-router-dom";
import { signUp, confirmSignUp, resendSignUpCode, signIn } from "aws-amplify/auth";
import { Brand } from "@championcare/ui";
import { COGNITO_ENABLED } from "../config";
import { useAppAuth } from "../auth";

const STATES = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut", "Delaware",
  "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa", "Kansas", "Kentucky",
  "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan", "Minnesota", "Mississippi",
  "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire", "New Jersey", "New Mexico",
  "New York", "North Carolina", "North Dakota", "Ohio", "Oklahoma", "Oregon", "Pennsylvania",
  "Rhode Island", "South Carolina", "South Dakota", "Tennessee", "Texas", "Utah", "Vermont",
  "Virginia", "Washington", "West Virginia", "Wisconsin", "Wyoming",
];

// The membership tier chosen on the marketing site arrives as ?tier= on the signup hand-off. Only
// the self-serve tiers are accepted here — fighter is invite-only; anything else is ignored (the
// API falls back to basic). We write it to the Cognito user as `custom:tier` (replacing the old
// auth provider's unsafe metadata) so JIT provisioning can assign it to the patient row. No
// payment here.
function selectedTier(): "basic" | "premium" | undefined {
  const t = new URLSearchParams(window.location.search).get("tier");
  return t === "basic" || t === "premium" ? t : undefined;
}

// Cognito self-serve patient signup: signUp (email + password + custom:tier) → email-code confirm
// (confirmSignUp) → auto signIn. The pool's SPA client can only write email + custom:tier
// (terraform/cognito), so the name is NOT collected here — members set it on the Profile page and
// the app backend owns it. Arriving with location.state.confirmEmail (from SignIn on an
// unconfirmed account) jumps straight to the confirm step.
function CognitoCreateAccount() {
  const location = useLocation();
  const handoffEmail = (location.state as { confirmEmail?: string } | null)?.confirmEmail;

  const [email, setEmail] = useState(handoffEmail ?? "");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [step, setStep] = useState<"form" | "confirm">(handoffEmail ? "confirm" : "form");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(
    handoffEmail ? "Your account isn’t verified yet — enter the code we emailed you." : null,
  );

  async function submitForm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const tier = selectedTier();
      const { nextStep } = await signUp({
        username: email.trim(),
        password,
        options: {
          userAttributes: {
            email: email.trim(),
            ...(tier ? { "custom:tier": tier } : {}),
          },
        },
      });
      if (nextStep.signUpStep === "CONFIRM_SIGN_UP") {
        setStep("confirm");
        setNotice(null);
      } else {
        // Pool auto-confirmed (not the drstack config, but harmless): sign straight in.
        await signIn({ username: email.trim(), password });
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "We couldn’t create your account.");
    } finally {
      setBusy(false);
    }
  }

  async function submitConfirm(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await confirmSignUp({ username: email.trim(), confirmationCode: code.trim() });
      if (password) {
        // Fresh signup in this session — complete the flow by signing in; the auth bridge's Hub
        // listener flips isSignedIn and the redirect takes over.
        await signIn({ username: email.trim(), password });
      } else {
        // Hand-off from SignIn (no password in memory): back to sign-in to finish.
        setNotice("Email verified — sign in to continue.");
        setStep("form");
        setCode("");
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "That code didn’t work. Please try again.");
    } finally {
      setBusy(false);
    }
  }

  async function resend() {
    setError(null);
    try {
      await resendSignUpCode({ username: email.trim() });
      setNotice("We sent a new code to your email.");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Couldn’t resend the code.");
    }
  }

  if (step === "confirm") {
    return (
      <div className="pt-auth">
        <div className="pt-auth__card">
          <div className="pt-auth__brand">
            <Brand size={28} />
          </div>
          <h1 className="pt-auth__title">
            Confirm your <em>email.</em>
          </h1>
          <p className="pt-auth__lede">
            We sent a 6-digit code to <strong>{email}</strong>. Enter it to verify your account.
          </p>
          <form onSubmit={submitConfirm}>
            <div className="pt-field">
              <label htmlFor="code">Verification code</label>
              <input
                id="code"
                className="pt-input"
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                placeholder="123456"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
                required
              />
            </div>
            {notice && <p className="pt-auth__note" role="status">{notice}</p>}
            {error && (
              <p className="pt-auth__note" role="alert" style={{ color: "var(--red)" }}>{error}</p>
            )}
            <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy || code.length < 6}>
              {busy ? "Verifying…" : "Verify →"}
            </button>
          </form>
          <p className="pt-auth__alt">
            Didn&rsquo;t get it?{" "}
            <button type="button" className="pt-auth__link" onClick={() => void resend()} style={{ background: "none", border: 0, padding: 0, cursor: "pointer" }}>
              Resend code
            </button>
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="pt-auth">
      <div className="pt-auth__card">
        <div className="pt-auth__brand">
          <Brand size={28} />
        </div>
        <h1 className="pt-auth__title">
          Join <em>Champion Care.</em>
        </h1>
        <p className="pt-auth__lede">Premium telehealth, on your schedule. Get started in under a minute.</p>

        <form onSubmit={submitForm}>
          <div className="pt-field">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              className="pt-input"
              type="email"
              autoComplete="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div className="pt-field">
            <label htmlFor="password">Password</label>
            <input
              id="password"
              className="pt-input"
              type="password"
              autoComplete="new-password"
              placeholder="12+ characters"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={12}
            />
            <p className="pt-field__hint">
              At least 12 characters with upper &amp; lower case, a number, and a symbol.
            </p>
          </div>
          {notice && <p className="pt-auth__note" role="status">{notice}</p>}
          {error && (
            <p className="pt-auth__note" role="alert" style={{ color: "var(--red)" }}>{error}</p>
          )}
          <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy}>
            {busy ? "Creating…" : "Create account →"}
          </button>
        </form>

        <p className="pt-auth__alt">
          Already have an account? <Link to="/sign-in" className="pt-auth__link">Sign in</Link>
        </p>
      </div>
    </div>
  );
}

export function CreateAccount() {
  const { isLoaded, isSignedIn, devSignIn } = useAppAuth();
  const [busy, setBusy] = useState(false);

  if (isLoaded && isSignedIn) return <Navigate to="/" replace />;

  if (COGNITO_ENABLED) return <CognitoCreateAccount />;

  const dev = async () => {
    setBusy(true);
    await devSignIn?.();
    setBusy(false);
  };
  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    void dev();
  };

  return (
    <div className="pt-auth">
      <div className="pt-auth__card">
        <div className="pt-auth__brand">
          <Brand size={28} />
        </div>
        <h1 className="pt-auth__title">
          Join <em>Champion Care.</em>
        </h1>
        <p className="pt-auth__lede">Premium telehealth, on your schedule. Get started in under a minute.</p>

        <form onSubmit={onSubmit}>
          <div className="pt-field">
            <label htmlFor="name">Full name</label>
            <input id="name" className="pt-input" autoComplete="name" placeholder="Your name" />
          </div>
          <div className="pt-field">
            <label htmlFor="email">Email</label>
            <input id="email" className="pt-input" type="email" autoComplete="email" placeholder="you@example.com" />
          </div>
          <div className="pt-field">
            <label htmlFor="password">Password</label>
            <input id="password" className="pt-input" type="password" autoComplete="new-password" placeholder="••••••••" />
          </div>
          <div className="pt-field">
            <label htmlFor="state">Where do you live?</label>
            <select id="state" className="pt-input" defaultValue="">
              <option value="" disabled>
                Select your state
              </option>
              {STATES.map((s) => (
                <option key={s} value={s}>
                  {s}
                </option>
              ))}
            </select>
            <p className="pt-field__hint">We use this to match you with a licensed physician in your state.</p>
          </div>
          <button type="submit" className="pt-btn pt-btn--primary pt-auth__submit" disabled={busy}>
            {busy ? "Creating…" : "Create account →"}
          </button>
        </form>

        <p className="pt-auth__alt">
          Already have an account? <Link to="/sign-in" className="pt-auth__link">Sign in</Link>
        </p>
        <p className="pt-auth__note">
          Dev environment — account creation is simulated with a local session (no Cognito configured).
        </p>
      </div>
    </div>
  );
}
